import { RowActions } from './RowActions';
import { GripVertical, Loader2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';

/**
 * A board whose columns are stages, and whose cards move between them.
 *
 * ## Moving a card is firing an event, not setting a status
 *
 * This is the whole design, and the thing most likely to be undone by somebody
 * in a hurry. Status here is the *consequence* of an event — `dispatch`,
 * `start_travel`, `finish` — and each event carries guards: a job cannot be
 * finished without a report, a technician cannot accept somebody else's work.
 * Writing a status directly on drop would make this the one screen where those
 * rules did not apply, and the audit trail would record a state nobody moved
 * through.
 *
 * So a drop asks `actionFor` what single action the move corresponds to. If
 * there is none, the column refuses the card *before* it is dropped — the
 * cursor says no, which is the only honest thing a board can do with a move
 * that is not allowed.
 *
 * ## Why drag and drop is never the only way
 *
 * Dragging needs a pointer, a steady hand and a screen wide enough to show two
 * columns at once. Every card therefore carries a plain menu of the same
 * moves, and that menu is what the keyboard and a phone use. The drag is an
 * accelerant for people who like it; it is not the interface.
 *
 * Built on the browser's own drag events rather than a library. What this
 * needs is a fifth of any drag-and-drop package, the page's CSP blocks most
 * CDNs anyway, and a dependency for this would be a dependency to keep.
 */

export type KanbanColumn<Row> = {
  key: string;
  label: string;
  /** Rows that belong here. */
  holds: (row: Row) => boolean;
  /** Shown under the count, when the column deserves a word of explanation. */
  note?: string;
};

export function Kanban<Row>({
  rows,
  columns,
  rowKey,
  card,
  actionFor,
  onMove,
  moving,
  emptyColumn = 'Nothing here',
}: {
  rows: Row[];
  columns: KanbanColumn<Row>[];
  rowKey: (row: Row) => string;
  card: (row: Row) => ReactNode;
  /**
   * The action that would move this row to this column, or null if none does.
   *
   * Asked before a drop is allowed and again to build each card's menu, so the
   * two can never offer different moves.
   */
  actionFor: (row: Row, column: KanbanColumn<Row>) => { event: string; label: string } | null;
  onMove: (row: Row, column: KanbanColumn<Row>, event: string) => void;
  /** The row currently being moved, so its card can say so. */
  moving?: string | null;
  emptyColumn?: string;
}) {
  const [dragging, setDragging] = useState<string | null>(null);
  /** The column the pointer is over, and whether it would take the card. */
  const [over, setOver] = useState<{ key: string; allowed: boolean } | null>(null);

  const held = dragging ? rows.find((row) => rowKey(row) === dragging) ?? null : null;

  return (
    <div className="flex gap-3 overflow-x-auto pb-2" data-kanban>
      {columns.map((column) => {
        const mine = rows.filter((row) => column.holds(row));
        const wouldTake = held ? actionFor(held, column) : null;
        const isOver = over?.key === column.key;

        return (
          <section
            key={column.key}
            data-kanban-column={column.key}
            /*
             * `min-w` rather than a grid: five columns on a laptop is a
             * horizontal scroll, and a scroll is honest where squeezing five
             * columns into a phone's width is not.
             */
            className={`flex w-72 min-w-[18rem] flex-col rounded-xl border p-2 transition-colors ${
              isOver && over.allowed
                ? 'border-primary-400 bg-primary-50'
                : isOver && !over.allowed
                  ? 'border-danger-300 bg-danger-50'
                  : 'border-border bg-muted/30'
            }`}
            onDragOver={(event) => {
              /*
               * `preventDefault` is what marks a drop target in the browser's
               * model, so it is called only when the move is legal. That is
               * also what makes the cursor show "no entry" over a column that
               * will not take the card — the refusal is visible before
               * anything happens rather than after.
               */
              if (wouldTake) event.preventDefault();
              setOver({ key: column.key, allowed: Boolean(wouldTake) });
            }}
            onDragLeave={() => setOver((was) => (was?.key === column.key ? null : was))}
            onDrop={(event) => {
              event.preventDefault();
              setOver(null);
              if (held && wouldTake) onMove(held, column, wouldTake.event);
              setDragging(null);
            }}
          >
            <header className="px-1 pb-2">
              <h3 className="flex items-baseline justify-between gap-2 font-heading text-sm">
                {column.label}
                <span className="tabular-nums text-muted-foreground">{mine.length}</span>
              </h3>
              {column.note && (
                <p className="mt-0.5 text-xs text-muted-foreground">{column.note}</p>
              )}
            </header>

            <div className="flex min-h-[4rem] flex-col gap-2">
              {mine.length === 0 && (
                <p className="px-1 py-4 text-center text-xs text-muted-foreground">
                  {emptyColumn}
                </p>
              )}

              {mine.map((row) => {
                const key = rowKey(row);
                const busy = moving === key;

                /* The same question the drop asks, for the menu. */
                const moves = columns
                  .filter((other) => other.key !== column.key)
                  .map((other) => ({ column: other, action: actionFor(row, other) }))
                  .filter((one): one is { column: KanbanColumn<Row>; action: { event: string; label: string } } =>
                    Boolean(one.action),
                  );

                return (
                  <article
                    key={key}
                    draggable={moves.length > 0 && !busy}
                    onDragStart={() => setDragging(key)}
                    onDragEnd={() => {
                      setDragging(null);
                      setOver(null);
                    }}
                    className={`rounded-lg border border-border bg-card p-2 text-sm ${
                      dragging === key ? 'opacity-40' : ''
                    } ${moves.length > 0 && !busy ? 'cursor-grab active:cursor-grabbing' : ''}`}
                  >
                    <div className="flex items-start gap-1.5">
                      {moves.length > 0 && (
                        <GripVertical
                          className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                          aria-hidden="true"
                        />
                      )}
                      <div className="min-w-0 flex-1">{card(row)}</div>
                      {busy && (
                        <Loader2
                          className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-primary-500"
                          aria-hidden="true"
                        />
                      )}
                    </div>

                    {/*
                      * The same moves, reachable without a pointer — now in
                      * the overflow rather than as a control on every card.
                      *
                      * The select was right about the requirement and wrong
                      * about the cost. Dragging is the primary way to move a
                      * card, so the board carried a second, full-width way to
                      * do the same thing on every card, roughly doubling the
                      * height of each one and halving how many fit on a
                      * screen — on the view whose whole purpose is seeing the
                      * work at once.
                      *
                      * Behind ⋯ it keeps everything that mattered: reachable
                      * by keyboard, reachable on touch, and still offering
                      * exactly the moves dragging offers. It costs one press
                      * and gives back half the board.
                      */}
                    {moves.length > 0 && (
                      <div className="mt-2">
                        <RowActions
                          actions={moves.map((one) => ({
                            id: one.column.key,
                            label: one.action.label,
                            disabled: busy,
                            title: `Move to ${one.column.label}`,
                            onClick: () => onMove(row, one.column, one.action.event),
                          }))}
                        />
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
